// src/index.ts
function getOrThrow() {
  if (this.isSuccess)
    return this.value;
  throw this.error;
}
function toUnion() {
  if (this.isSuccess)
    return this.value;
  return this.error;
}
function ifSuccess(f) {
  if (this.isFailure)
    return void 0;
  return f(this.value);
}
function ifFailure(f) {
  if (this.isSuccess)
    return void 0;
  return f(this.error);
}
function match(f, g) {
  if (this.isSuccess)
    return f(this.value);
  return g(this.error);
}
function map(f) {
  if (this.isFailure)
    return this;
  return Result.success(f(this.value));
}
function mapError(f) {
  if (this.isSuccess)
    return this;
  return Result.failure(f(this.error));
}
function flatMap(f) {
  if (this.isFailure)
    return this;
  return f(this.value);
}
function flatten() {
  if (this.isFailure)
    return this;
  return this.value;
}
function assertErrorInstanceOf(constructor) {
  if (this.isSuccess)
    return this;
  if (this.error instanceof constructor)
    return this;
  throw new TypeError(`Assertion failed: Expected error to be an instance of ${constructor.name}.`);
}
var prototype = {
  /**
   * Returns `this.value` if `this` is a successful result, otherwise throws `this.error`.
   * @example Returns the payload of a successful result.
   * Result.success(123).getOrThrow() // 123
   * @example Throws the payload of a failed result.
   * Result.failure('error').getOrThrow() // throws 'error'
   */
  getOrThrow,
  /**
   * Returns the payload of the result.
   * @example Returns the payload of a successful result.
   * Result.success(123).toUnion() // 123
   * @example Returns the payload of a failed result.
   * Result.failure('error').toUnion() // 'error'
   */
  toUnion,
  /**
   * Applies the given function to this.value if it's a successful result, otherwise returns undefined.
   * @example
   * Result.success(123).ifSuccess((x) => x * 2) // 246
   * Result.failure('error').ifSuccess((x: number) => x * 2) // undefined
   */
  ifSuccess,
  /**
   * Applies the given function to this.error if it's a failed result, otherwise returns undefined.
   * @example
   * Result.success(123).ifFailure((x: string) => x + '!') // undefined
   * Result.failure('error').ifFailure((x) => x + '!') // 'error!'
   */
  ifFailure,
  /**
   * Return the result of applying one of the given functions to the payload.
   * @example
   * Result.success(123).match((x) => x * 2, (x: string) => x + '!') // 246
   * Result.failure('error').match((x: number) => x * 2, (x) => x + '!') // 'error!'
   */
  match,
  /**
   * Creates a Result value by modifying the payload of the successful result using the given function.
   * @example
   * Result.success(123).map((x) => x * 2) // Result.success(246)
   * Result.failure('error').map((x: number) => x * 2) // Result.failure('error')
   */
  map,
  /**
   * Creates a Result value by modifying the payload of the failed result using the given function.
   * @example
   * Result.success(123).mapError((x: string) => x + '!') // Result.success(123)
   * Result.failure('error').mapError((x) => x + '!') // Result.failure('error!')
   */
  mapError,
  /**
   * Maps the payload of the successful result and flattens the nested Result type.
   * @example
   * Result.success(123).flatMap((x) => Result.success(x * 2)) // Result.success(246)
   * Result.success(123).flatMap((x) => Result.failure('error')) // Result.failure('error')
   * Result.failure('error').flatMap((x: number) => Result.success(x * 2)) // Result.failure('error')
   * Result.failure('error').flatMap((x) => Result.failure('failure')) // Result.failure('error')
   */
  flatMap,
  /**
   * Flattens the nested Result type.
   * @example
   * Result.success(Result.success(123)).flatten() // Result.success(123)
   * Result.success(Result.failure('error')).flatten() // Result.failure('error')
   * Result.failure('error').flatten() // Result.failure('error')
   */
  flatten,
  /**
   * Perform a safe cast of the error type to the given class. If the payload of the failed result is not instance of constructor, throws TypeError.
   * @example
   * const result: Result<number, Error> = Result.tryCatch(() => {
   *   if (Math.random() >= 0) {
   *     throw new Error('error')
   *   } else {
   *     return 123
   *   }
   * }).assertErrorInstanceOf(Error)
   */
  assertErrorInstanceOf
};
var Result;
((Result2) => {
  function success(value) {
    return withPrototype({ value, isSuccess: true, isFailure: false }, prototype);
  }
  Result2.success = success;
  function failure(error) {
    return withPrototype({ error, isSuccess: false, isFailure: true }, prototype);
  }
  Result2.failure = failure;
  function tryCatch(f) {
    try {
      return success(f());
    } catch (error) {
      return failure(error);
    }
  }
  Result2.tryCatch = tryCatch;
  async function fromPromise(promise) {
    try {
      return success(await promise);
    } catch (error) {
      return failure(error);
    }
  }
  Result2.fromPromise = fromPromise;
  function fromNullish(value) {
    return value != null ? success(value) : failure(value);
  }
  Result2.fromNullish = fromNullish;
  function all(results) {
    const values = [];
    for (const result of results) {
      if (result.isFailure)
        return result;
      values.push(result.value);
    }
    return success(values);
  }
  Result2.all = all;
})(Result || (Result = {}));
function withPrototype(target, prototype2) {
  return Object.assign(Object.create(prototype2), target);
}
export {
  Result,
  prototype
};
